import { useEffect, useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Property } from '@immoaktas/api-client';
import { cityLine } from '../routes/landlord/parts/objects';
import { Icon, Logo, NavList, useNavActive, Wordmark, type IconName, type NavItem } from '../ui';
import { unitsLine, type PropertyFacts } from './navData';

/** House glyph of a property card: a single house, or a block of flats for owner flats and large buildings. */
const glyphOf = (p: Property): IconName =>
  p.type === 'weg_unit' || (p.unitCount ?? 0) >= 10 ? 'flats' : 'house';

/** Status dot tone of a property card (the text next to it carries the meaning, the dot only repeats it). */
const DOT: Record<string, string> = { warn: 'warn', err: 'err', ok: 'ok', brand: 'ok', neutral: 'neutral' };

/**
 * "Mehr ▸": the less used items of a level, folded under one toggle so the main list stays short (five items). It
 * opens by itself while one of its items is the current page; folded, it sums up the badges of its items.
 */
export function NavMore({ items, isActive, label }: { items: NavItem[]; isActive: (i: NavItem) => boolean; label: string }) {
  const { t } = useTranslation();
  const id = useId();
  const current = items.some(isActive);
  const [open, setOpen] = useState(current);
  useEffect(() => {
    if (current) setOpen(true);
  }, [current]);
  const badge = items.reduce((s, i) => s + (typeof i.badge === 'number' ? i.badge : 0), 0);
  return (
    <div className="nav-more-group">
      <button type="button" className={`nav-more-toggle${current && !open ? ' active' : ''}`} aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
        <span className="nav-label">
          <Icon name="more" size={18} />
          {label}
        </span>
        <span className="nav-more-end">
          {!open && badge > 0 && (
            <span className="nav-badge">
              <span aria-hidden>{badge}</span>
              <span className="sr-only">{t('workspace.nav.moreBadge', { count: badge })}</span>
            </span>
          )}
          <Icon name={open ? 'chevronDown' : 'chevronRight'} size={16} strokeWidth={2} aria-hidden />
        </span>
      </button>
      <div id={id} hidden={!open} className="nav-more-items">
        <NavList items={items} isActive={isActive} />
      </div>
    </div>
  );
}

/**
 * Level 1 "Alle Objekte" (design/Sidebar.dc.html, level=portfolio): dark sidebar with the logo, five account-wide
 * items (Übersicht · Objekte · Belege · Abrechnungen · Zahlungen), "Mehr" for the rest (Mieter & Verträge, Zähler,
 * Fristen, Mitteilungen, Export, Einstellungen), then the landlord's properties as small cards (one click opens
 * level 2) and "Objekt anlegen".
 */
export function PortfolioNav({
  main,
  more,
  properties,
  facts,
  canCreate,
  top,
  footer,
}: {
  main: NavItem[];
  more: NavItem[];
  properties: Property[];
  facts: Map<string, PropertyFacts>;
  canCreate: boolean;
  top?: ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  const isActive = useNavActive([...main, ...more]);
  return (
    <nav className="sidebar level-nav portfolio" aria-label={t('shell.level.navAll')}>
      <Link to="/" className="brand" aria-label={t('shell.level.home')}>
        <Logo size={28} />
        <Wordmark height={16} />
      </Link>
      {top}
      <span className="nav-section-title with-icon">
        <Icon name="portfolio" size={14} strokeWidth={1.8} />
        {t('shell.level.all')}
      </span>
      <NavList items={main} isActive={isActive} />
      <NavMore items={more} isActive={isActive} label={t('workspace.nav.more')} />

      <div className="nav-group-head">
        <h2 className="nav-section-title" id="nav-props-h">
          {t('shell.level.yours')}
        </h2>
      </div>
      <ul className="prop-links" aria-labelledby="nav-props-h">
        {properties.map((p) => {
          const f = facts.get(p.id);
          return (
            <li key={p.id}>
              <Link to={`/objekte/${p.id}`} className="prop-link">
                <span className="pl-glyph" aria-hidden>
                  <Icon name={glyphOf(p)} size={18} />
                </span>
                <span className="pl-text">
                  <span className="pl-name">{p.name}</span>
                  <span className="pl-meta">
                    {f?.status && (
                      <span className={`pl-dot ${DOT[f.status.tone] ?? 'neutral'}`} aria-hidden />
                    )}
                    <span className="truncate">{unitsLine(t, f)}</span>
                    {f?.status && <span className="sr-only">, {f.status.label}</span>}
                  </span>
                </span>
                <Icon name="chevronRight" size={14} strokeWidth={2} className="pl-chev" aria-hidden />
              </Link>
            </li>
          );
        })}
        {canCreate && (
          <li>
            <Link to="/objekte/neu" className="prop-add">
              <Icon name="plus" size={16} strokeWidth={2} aria-hidden />
              {t('shell.level.create')}
            </Link>
          </li>
        )}
      </ul>

      <div className="spacer" />
      {footer && <div className="sidebar-foot">{footer}</div>}
    </nav>
  );
}

/**
 * Level 2 "Im Objekt" (design/Sidebar.dc.html, level=objekt): light mint sidebar with a petrol band, the big way back
 * to "Alle Objekte", the property card (where you are, its state, switcher), six sections of this property
 * (Übersicht · Einheiten · Belege · Abrechnung · Zahlungen · Zähler), "Mehr" for the rest and the account items under
 * "Allgemein" like level 1. Level 3 (a unit) keeps this sidebar with "Einheiten" highlighted; the unit switcher and
 * the unit's tabs are in the page header.
 */
export function PropertyNav({
  property,
  facts,
  items,
  more,
  general,
  switcher,
  footer,
}: {
  property: Property;
  facts?: PropertyFacts;
  items: NavItem[];
  more: NavItem[];
  general: NavItem[];
  switcher?: ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  const isActive = useNavActive([...items, ...more, ...general]);
  const place = cityLine(property);
  return (
    <nav
      className="sidebar level-nav objekt"
      aria-label={t('shell.level.navProperty', { name: property.name })}
    >
      <Link to="/" className="level-back">
        <Icon name="arrowLeft" size={18} strokeWidth={2} />
        {t('shell.level.all')}
      </Link>
      {/* compact: name (as the switcher) and one fact line; the details are on the property's Übersicht */}
      <section className="here-card" aria-label={`${t('shell.level.youAreIn')}: ${property.name}`}>
        <span className="here-glyph" aria-hidden>
          <Icon name={glyphOf(property)} size={20} strokeWidth={1.6} />
        </span>
        <span className="here-titles">
          {switcher ?? <span className="here-name">{property.name}</span>}
          <span className="here-facts">
            {place && <span>{place}</span>}
            <span>{unitsLine(t, facts)}</span>
          </span>
        </span>
      </section>
      <div className="nav-section" role="group" aria-labelledby="nav-in-h">
        <span className="nav-section-title" id="nav-in-h">
          {t('shell.level.inThis')}
        </span>
        <NavList items={items} isActive={isActive} />
        <NavMore items={more} isActive={isActive} label={t('workspace.nav.more')} />
      </div>
      <div className="spacer" />
      <div className="nav-section" role="group" aria-labelledby="nav-general-h">
        <span className="nav-section-title" id="nav-general-h">
          {t('shell.section.account')}
        </span>
        <NavList items={general} isActive={isActive} />
      </div>
      {footer && <div className="sidebar-foot">{footer}</div>}
    </nav>
  );
}

